import axios from "axios";
import { GoogleAdsAuthManager } from "../../utils/auth";

const API_BASE_URL = "http://localhost:5000/api";

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth credentials
client.interceptors.request.use(
  (config) => {
    const authData = GoogleAdsAuthManager.getAuthData();
    if (authData && authData.accessToken) {
      console.log(authData.accessToken);
      config.headers.Authorization = `Bearer ${authData.accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, clear auth data
      GoogleAdsAuthManager.clearAuthData();
      GoogleAdsAuthManager.updateConnectionStatus(false);
      
      // Optionally trigger a page reload or redirect to reauth
      if (window.location.pathname !== '/') {
        console.log("Authentication expired, please reconnect");
      }
    }
    return Promise.reject(error);
  }
);

const ApiService = {
  // Auth methods
  async startGoogleAuth(userId?: string) {
    try {
      const actualUserId = userId || GoogleAdsAuthManager.getAuthData();
      console.log(actualUserId,"actualUserId from api service");
      const response = await client.get(`/auth/google?userId=${actualUserId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to start auth");
    }
  },

  async handleAuthCallback(code: any, state: any) {
    try {
      const response = await client.post("/auth/google/callback", {
        code,
        state,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Authentication failed");
    }
  },

  async getAuthStatus(userId?: string) {
    try {
      const actualUserId = userId || GoogleAdsAuthManager.getUserId();
      const response = await client.get(`/auth/status?userId=${actualUserId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to check status");
    }
  },

  async disconnectGoogleAds(userId?: string) {
    try {
      const actualUserId = userId || GoogleAdsAuthManager.getUserId();
      const response = await client.post("/auth/disconnect", { userId: actualUserId });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to disconnect");
    }
  },

  // Google Ads data methods
  async getAccounts(userId?: string) {
    try {
      const actualUserId = userId || GoogleAdsAuthManager.getUserId();
      const response = await client.get(
        `/google-ads/accounts?userId=${actualUserId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to get accounts");
    }
  },

  async getCampaigns(
    customerId: any,
    userId?: string,
    dateRange = "LAST_30_DAYS"
  ) {
    try {
      const actualUserId = userId || GoogleAdsAuthManager.getUserId();
      const response = await client.get(
        `/google-ads/campaigns?customerId=${customerId}&userId=${actualUserId}&dateRange=${dateRange}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to get campaigns");
    }
  },

  async getAccountOverview(customerId: string, userId?: string) {
    try {
      const actualUserId = userId || GoogleAdsAuthManager.getUserId();
      const response = await client.get(
        `/google-ads/overview?customerId=${customerId}&userId=${actualUserId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to get overview");
    }
  },

  async getKeywords(
    customerId: string,
    userId?: string,
    campaignId: string | null = null
  ) {
    try {
      const actualUserId = userId || GoogleAdsAuthManager.getUserId();
      let url = `/google-ads/keywords?customerId=${customerId}&userId=${actualUserId}`;
      if (campaignId) {
        url += `&campaignId=${campaignId}`;
      }
      const response = await client.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to get keywords");
    }
  },
};

export default ApiService;
